import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, RefreshCw, Layers, Trash2, Loader2, Wallet } from 'lucide-react';
import { CexAccount, SUPPORTED_CEX } from '../constants';
import { ALL_ACCOUNTS_ID, ALL_ACCOUNTS_OBJECT, useCexAccount, WalletAccount } from '../context/CexAccountContext';
import { SUPPORTED_WALLET_NETWORKS } from '../config/wallet';
import { API_BASE_URL } from '../config/api';

interface CexAccountSwitcherProps {
  selectedAccount: CexAccount | null;
  onAccountChange: (account: CexAccount) => void;
  onAddAccount: () => void;
  onAddWallet?: () => void;
  refreshTrigger?: number;
}

const CexAccountSwitcher: React.FC<CexAccountSwitcherProps> = ({
  selectedAccount,
  onAccountChange,
  onAddAccount,
  onAddWallet,
  refreshTrigger,
}) => {
  const {
    accounts, fetchAccounts, isLoading, setSelectedAccount,
    walletAccounts, fetchWalletAccounts,
    selectedWalletAccount, setSelectedWalletAccount,
  } = useCexAccount();

  const [isOpen, setIsOpen] = useState(false);

  // CEX delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Wallet delete state
  const [confirmDeleteWalletId, setConfirmDeleteWalletId] = useState<number | null>(null);
  const [deletingWalletId, setDeletingWalletId] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchAccounts(); }, [refreshTrigger]);

  const closeDropdown = () => {
    setIsOpen(false);
    setConfirmDeleteId(null);
    setConfirmDeleteWalletId(null);
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getCexIcon = (n: string) => SUPPORTED_CEX.find(c => c.value === n)?.icon;
  const getCexColor = (n: string) => SUPPORTED_CEX.find(c => c.value === n)?.color || '#666';
  const getNetworkIcon = (n: string) => SUPPORTED_WALLET_NETWORKS.find(w => w.value === n)?.icon;
  const getNetworkColor = (n: string) => SUPPORTED_WALLET_NETWORKS.find(w => w.value === n)?.color || '#666';

  const handleAccountSelect = (account: CexAccount) => {
    localStorage.setItem('selectedAccountId', account.id.toString());
    onAccountChange(account);
    closeDropdown();
  };

  const handleAllAccountsSelect = () => {
    localStorage.setItem('selectedAccountId', ALL_ACCOUNTS_ID.toString());
    onAccountChange(ALL_ACCOUNTS_OBJECT);
    closeDropdown();
  };

  // Click outside → close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) closeDropdown();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    fetchAccounts();
    fetchWalletAccounts();
  };

  // ── CEX delete handlers ────────────────────────────────────────────────────
  const handleDeleteClick = (e: React.MouseEvent, id: number) => { e.stopPropagation(); setConfirmDeleteId(id); };
  const handleDeleteCancel = (e: React.MouseEvent) => { e.stopPropagation(); setConfirmDeleteId(null); };

  const handleDeleteConfirm = async (e: React.MouseEvent, account: CexAccount) => {
    e.stopPropagation();
    setDeletingId(account.id);
    setConfirmDeleteId(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/cex-accounts/${account.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const updated = await fetchAccounts() ?? [];
        if (selectedAccount?.id === account.id) {
          const next = updated[0] ?? ALL_ACCOUNTS_OBJECT;
          setSelectedAccount(next);
          onAccountChange(next);
        }
      }
    } catch (err) { console.error(err); }
    finally { setDeletingId(null); }
  };

  // ── Wallet delete handlers ─────────────────────────────────────────────────
  const handleDeleteWalletClick = (e: React.MouseEvent, id: number) => { e.stopPropagation(); setConfirmDeleteWalletId(id); };
  const handleDeleteWalletCancel = (e: React.MouseEvent) => { e.stopPropagation(); setConfirmDeleteWalletId(null); };

  const handleDeleteWalletConfirm = async (e: React.MouseEvent, wallet: WalletAccount) => {
    e.stopPropagation();
    setDeletingWalletId(wallet.id);
    setConfirmDeleteWalletId(null);
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_BASE_URL}/api/wallet-accounts/${wallet.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchWalletAccounts();
    } catch (err) { console.error(err); }
    finally { setDeletingWalletId(null); }
  };

  // ── Empty / loading states ─────────────────────────────────────────────────
  if (isLoading && accounts.length === 0) {
    return (
      <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2">
        <RefreshCw size={16} className="animate-spin text-neutral-500" />
        <span className="text-sm text-neutral-400">Loading...</span>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-800 rounded-lg px-4 py-2">
        <span className="text-sm text-neutral-400">No accounts</span>
        <button onClick={onAddAccount} className="text-xs text-red-500 hover:text-red-400 font-medium transition-colors">
          Add one
        </button>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="relative" ref={dropdownRef}>

      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-3 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 rounded-lg px-4 py-2 transition-colors min-w-[200px]"
      >
        {selectedWalletAccount ? (
          <>
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${getNetworkColor(selectedWalletAccount.network_name)}20` }}
            >
              {getNetworkIcon(selectedWalletAccount.network_name)
                ? <img src={getNetworkIcon(selectedWalletAccount.network_name)} alt={selectedWalletAccount.network_display_name} className="w-5 h-5 object-contain" />
                : <Wallet size={18} style={{ color: getNetworkColor(selectedWalletAccount.network_name) }} />
              }
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-white truncate">{selectedWalletAccount.network_display_name}</p>
              <p className="text-xs text-neutral-500 font-mono truncate">
                {selectedWalletAccount.wallet_address.slice(0, 8)}…{selectedWalletAccount.wallet_address.slice(-4)}
              </p>
            </div>
            <ChevronDown size={16} className={`text-neutral-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          </>
        ) : selectedAccount ? (
          <>
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: selectedAccount.id === ALL_ACCOUNTS_ID
                  ? '#ef444420'
                  : `${getCexColor(selectedAccount.cex_name)}20`,
              }}
            >
              {selectedAccount.id === ALL_ACCOUNTS_ID ? (
                <Layers size={18} className="text-red-500" />
              ) : getCexIcon(selectedAccount.cex_name) ? (
                <img src={getCexIcon(selectedAccount.cex_name)} alt={selectedAccount.cex_display_name} className="w-5 h-5 object-contain" />
              ) : (
                <span className="text-xs font-bold" style={{ color: getCexColor(selectedAccount.cex_name) }}>
                  {selectedAccount.cex_display_name[0]}
                </span>
              )}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-white truncate">{selectedAccount.cex_display_name}</p>
              <p className="text-xs text-neutral-500 truncate">{selectedAccount.account_label}</p>
            </div>
            <ChevronDown size={16} className={`text-neutral-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          </>
        ) : null}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-neutral-900 border border-neutral-800 rounded-lg shadow-xl z-[100] overflow-hidden min-w-[280px]">

          {/* Header */}
          <div className="p-2 border-b border-neutral-800 flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-400">Accounts & Wallets</span>
            <button onClick={handleRefresh} className="p-1 hover:bg-neutral-800 rounded-md transition-colors">
              <RefreshCw size={14} className={`text-neutral-500 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Scrollable list */}
          <div className="max-h-[360px] overflow-y-auto" data-lenis-prevent>

            {/* All Accounts */}
            <button
              onClick={handleAllAccountsSelect}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-800 transition-colors text-left ${selectedAccount?.id === ALL_ACCOUNTS_ID ? 'bg-neutral-800/50' : ''}`}
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center bg-red-500/10 flex-shrink-0">
                <Layers size={18} className="text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">All Accounts</p>
                <p className="text-xs text-neutral-500">Unified View</p>
              </div>
              {selectedAccount?.id === ALL_ACCOUNTS_ID && <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
            </button>

            {/* ── CEX Accounts ── */}
            {accounts.length > 0 && (
              <>
                <div className="px-3 py-1.5 flex items-center gap-2">
                  <span className="text-[9px] font-bold text-neutral-600 uppercase tracking-widest">CEX Accounts</span>
                  <div className="flex-1 h-[1px] bg-neutral-800" />
                </div>

                {accounts.map(account => (
                  <div
                    key={account.id}
                    className={`group flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-800 transition-colors ${selectedAccount?.id === account.id ? 'bg-neutral-800/50' : ''}`}
                  >
                    <button onClick={() => handleAccountSelect(account)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${getCexColor(account.cex_name)}20` }}>
                        {getCexIcon(account.cex_name)
                          ? <img src={getCexIcon(account.cex_name)} alt={account.cex_display_name} className="w-5 h-5 object-contain" />
                          : <span className="text-sm font-bold" style={{ color: getCexColor(account.cex_name) }}>{account.cex_display_name[0]}</span>
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{account.full_display_name}</p>
                        <p className="text-xs text-neutral-500">{account.is_active ? 'Active' : 'Inactive'}</p>
                      </div>
                      {selectedAccount?.id === account.id && <div className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />}
                    </button>

                    {/* CEX delete */}
                    <div className="flex-shrink-0 flex items-center">
                      {deletingId === account.id ? (
                        <Loader2 size={15} className="text-red-400 animate-spin" />
                      ) : confirmDeleteId === account.id ? (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <span className="text-[10px] text-amber-400 font-medium whitespace-nowrap">Remove?</span>
                          <button onClick={e => handleDeleteConfirm(e, account)} className="text-[10px] px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded font-bold transition-colors">Yes</button>
                          <button onClick={handleDeleteCancel} className="text-[10px] px-1.5 py-0.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded font-bold transition-colors">No</button>
                        </div>
                      ) : (
                        <button onClick={e => handleDeleteClick(e, account.id)} className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/10 transition-all">
                          <Trash2 size={14} className="text-neutral-500 hover:text-red-400 transition-colors" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* ── On-Chain Wallets ── */}
            {walletAccounts.length > 0 && (
              <>
                <div className="px-3 py-1.5 flex items-center gap-2">
                  <span className="text-[9px] font-bold text-neutral-600 uppercase tracking-widest">On-Chain Wallets</span>
                  <div className="flex-1 h-[1px] bg-neutral-800" />
                </div>

                {walletAccounts.map(wallet => (
                  <div key={`wallet-${wallet.id}`} className="group flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-800 transition-colors">
                    <button
                      onClick={() => {
                        setSelectedWalletAccount(wallet);
                        // Trigger background sync when wallet is selected
                        const token = localStorage.getItem('auth_token');
                        if (token) {
                          fetch(`${API_BASE_URL}/api/wallet-accounts/${wallet.id}/sync`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` },
                          }).then(() => fetchWalletAccounts()).catch(() => {});
                        }
                        closeDropdown();
                      }}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${getNetworkColor(wallet.network_name)}20` }}>
                        {getNetworkIcon(wallet.network_name)
                          ? <img src={getNetworkIcon(wallet.network_name)} alt={wallet.network_display_name} className="w-5 h-5 object-contain" />
                          : <Wallet size={14} style={{ color: getNetworkColor(wallet.network_name) }} />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{wallet.full_display_name}</p>
                        <p className="text-xs text-neutral-500 font-mono truncate">
                          {wallet.wallet_address.slice(0, 8)}…{wallet.wallet_address.slice(-4)}
                        </p>
                      </div>
                      {selectedWalletAccount?.id === wallet.id && (
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />
                      )}
                    </button>

                    {/* Wallet delete */}
                    <div className="flex-shrink-0 flex items-center">
                      {deletingWalletId === wallet.id ? (
                        <Loader2 size={15} className="text-red-400 animate-spin" />
                      ) : confirmDeleteWalletId === wallet.id ? (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <span className="text-[10px] text-amber-400 font-medium whitespace-nowrap">Remove?</span>
                          <button onClick={e => handleDeleteWalletConfirm(e, wallet)} className="text-[10px] px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded font-bold transition-colors">Yes</button>
                          <button onClick={handleDeleteWalletCancel} className="text-[10px] px-1.5 py-0.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded font-bold transition-colors">No</button>
                        </div>
                      ) : (
                        <button onClick={e => handleDeleteWalletClick(e, wallet.id)} className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/10 transition-all">
                          <Trash2 size={14} className="text-neutral-500 hover:text-red-400 transition-colors" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Add buttons */}
          <div className="border-t border-neutral-800 p-3 space-y-2">
            <button
              onClick={() => { closeDropdown(); onAddAccount(); }}
              className="w-full flex items-center justify-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white py-2 rounded-lg transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Add CEX Account
            </button>
            {onAddWallet && (
              <button
                onClick={() => { closeDropdown(); onAddWallet(); }}
                className="w-full flex items-center justify-center gap-2 bg-neutral-800/60 hover:bg-neutral-700 text-neutral-300 py-2 rounded-lg transition-colors text-sm font-medium border border-neutral-700"
              >
                <Wallet size={16} />
                Add Wallet
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CexAccountSwitcher;
